import React, { useState } from 'react';
import { ArrowLeft, Filter, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { projects } from '../mock';

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [hoveredId, setHoveredId] = useState(null);
  
  const categories = ['Alle', 'Sanierung', 'Schlüsselfertig', 'Innenausbau'];
  
  const filteredProjects = selectedCategory === 'Alle'
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          className="mb-8 border-2 border-black hover:bg-black hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Startseite
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-black to-[#00CCDD] text-white px-6 py-2 rounded-full mb-6">
            <Filter className="w-4 h-4" />
            <span className="font-semibold text-sm uppercase tracking-wide">Alle Referenzen</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6">
            Unsere Projekte
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Über 180 erfolgreich abgeschlossene Bauprojekte - Ein Überblick über unsere Arbeit
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`relative px-8 py-3 rounded-full font-semibold transition-all duration-300 overflow-hidden group ${
                selectedCategory === category
                  ? 'text-white'
                  : 'text-gray-700 bg-white hover:bg-gray-50 border-2 border-gray-200'
              }`}
            >
              {selectedCategory === category && (
                <div className="absolute inset-0 bg-gradient-to-r from-black to-[#00CCDD]"></div>
              )}
              <span className="relative z-10">{category}</span>
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="group relative"
              onMouseEnter={() => setHoveredId(project.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className={`w-full h-full object-cover transition-transform duration-700 ${
                      hoveredId === project.id ? 'scale-110' : 'scale-100'
                    }`}
                  />
                  
                  <div className={`absolute inset-0 bg-gradient-to-br from-black/90 via-black/80 to-[#00CCDD]/70 transition-opacity duration-500 ${
                    hoveredId === project.id ? 'opacity-90' : 'opacity-0'
                  }`}>
                    <div className="absolute inset-0 opacity-20">
                      <div className="absolute top-0 left-0 w-full h-full transform skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent"></div>
                    </div>
                  </div>

                  <div className="absolute top-4 right-4 bg-gradient-to-r from-black to-[#00CCDD] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    {project.year}
                  </div>

                  {hoveredId === project.id && (
                    <div className="absolute inset-0 flex items-center justify-center p-6">
                      <div className="text-center text-white space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h3 className="text-2xl font-bold">{project.title}</h3>
                        <p className="text-white/90">{project.description}</p>
                        <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                          <ExternalLink className="w-4 h-4" />
                          <span className="text-sm font-semibold">Details ansehen</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="mb-3">
                    <span className="inline-block bg-gradient-to-r from-[#E6F7FF] to-[#CCF2FF] text-black text-xs font-bold px-4 py-1.5 rounded-full border border-[#00CCDD]/30">
                      {project.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2 group-hover:text-[#00CCDD] transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-black via-[#00CCDD] to-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-black to-[#00CCDD] rounded-3xl p-12 text-center text-white">
          <h3 className="text-3xl sm:text-4xl font-bold mb-4">Ihr Projekt mit uns realisieren?</h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch
          </p>
          <Button
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            size="lg"
            className="bg-white text-black hover:bg-gray-100"
          >
            Jetzt Kontakt aufnehmen
          </Button>
        </div>
      </div>
    </div>
  );
};